import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from 'framer-motion';

/**
 * AgentAvatarSphere — the Phase 9 bioluminescent orb, unchanged.
 *
 * Rendered on consumer routes (/, /consumer/*). Sits at top-right (below the
 * TopNav), pulses idle drift, magnetic-attracts on hover, drags with elastic
 * snap-back to origin, and toggles an info popover on tap.
 *
 * This component USED to be `AgentAvatar` directly; Phase 12 extracted it so
 * the router-level dispatcher in AgentAvatar.jsx can switch between this
 * sphere (consumer) and the TCRobotAvatar (Talent Connect).
 *
 * Contract:
 *   props.tone            'idle' | 'active' | 'recognized'
 *   props.popoverContent  { eyebrow, title, rows: [{label, value, mono?}] }
 *
 * DO NOT tweak the physics or visual — the consumer surface UX depends on
 * this being pixel-identical to Phase 9.
 */

const MAGNETIC_RADIUS_DESKTOP = 220;
const MAGNETIC_RADIUS_MOBILE = 120;
const MAGNETIC_MAX_OFFSET = 24;

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
}

export default function AgentAvatarSphere({ tone, popoverContent }) {
  const reduce = useReducedMotion();

  const rootRef = useRef(null);
  const sphereRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [hoverBubble, setHoverBubble] = useState(false);

  const magneticX = useMotionValue(0);
  const magneticY = useMotionValue(0);
  const smoothX = useSpring(magneticX, { stiffness: 140, damping: 18, mass: 0.6 });
  const smoothY = useSpring(magneticY, { stiffness: 140, damping: 18, mass: 0.6 });

  useEffect(() => {
    if (reduce) return undefined;

    let queued = false;
    let lastEvt = null;
    let hidden = document.hidden;

    const onVis = () => { hidden = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    const compute = () => {
      queued = false;
      if (!lastEvt || !sphereRef.current || dragging || hidden) return;
      const rect = sphereRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = lastEvt.clientX - cx;
      const dy = lastEvt.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const radius = isMobileViewport() ? MAGNETIC_RADIUS_MOBILE : MAGNETIC_RADIUS_DESKTOP;
      if (dist > radius) {
        magneticX.set(0);
        magneticY.set(0);
        if (hoverBubble) setHoverBubble(false);
        return;
      }
      const strength = 1 - dist / radius;
      const targetX = (dx / dist) * MAGNETIC_MAX_OFFSET * strength;
      const targetY = (dy / dist) * MAGNETIC_MAX_OFFSET * strength;
      magneticX.set(Number.isFinite(targetX) ? targetX : 0);
      magneticY.set(Number.isFinite(targetY) ? targetY : 0);
      if (!hoverBubble) setHoverBubble(true);
    };

    const onMove = (e) => {
      lastEvt = e;
      if (queued) return;
      queued = true;
      requestAnimationFrame(compute);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [reduce, dragging, hoverBubble, magneticX, magneticY]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const handleTap = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const handleKey = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((o) => !o);
    }
  }, []);

  const handleDragStart = () => {
    setOpen(false);
    setDragging(true);
    magneticX.set(0);
    magneticY.set(0);
  };
  const handleDragEnd = () => { setDragging(false); };

  const driftPaused = dragging || hoverBubble || open || reduce;

  const dragProps = reduce
    ? {}
    : {
        drag: true,
        dragConstraints: { top: 0, bottom: 0, left: 0, right: 0 },
        dragElastic: 0.5,
        dragTransition: { bounceStiffness: 300, bounceDamping: 22 },
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        whileDrag: { cursor: 'grabbing' },
      };

  const magneticStyle = reduce ? {} : { x: smoothX, y: smoothY };

  return (
    <div className="sv-avatar-root" ref={rootRef} data-testid="agent-avatar-root">
      <motion.div
        {...dragProps}
        onTap={handleTap}
        className="sv-avatar-drag-layer"
        style={{ touchAction: reduce ? 'auto' : 'none' }}
        data-testid="agent-avatar-drag"
      >
        <motion.div className="sv-avatar-magnetic-layer" style={magneticStyle}>
          <div className={`sv-avatar-drift-layer ${driftPaused ? 'is-paused' : ''}`}>
            <button
              ref={sphereRef}
              type="button"
              className={`sv-avatar-sphere sv-avatar--${tone}`}
              aria-label="AI agent — click for status"
              aria-expanded={open}
              aria-haspopup="dialog"
              data-testid="agent-avatar"
              data-tone={tone}
              onKeyDown={handleKey}
            >
              <span className="sv-avatar-core" />
              <span className="sv-avatar-ring" />
              <span className="sv-avatar-particle sv-avatar-p1" />
              <span className="sv-avatar-particle sv-avatar-p2" />
              <span className="sv-avatar-particle sv-avatar-p3" />
              <span className="sv-avatar-particle sv-avatar-p4" />
            </button>
          </div>

          {open && popoverContent && (
            <motion.div
              className="sv-avatar-panel sv-glass"
              data-testid="agent-avatar-panel"
              role="dialog"
              aria-label="Agent information"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
            >
              <p className="sv-eyebrow mb-1">{popoverContent.eyebrow}</p>
              <h3 className="sv-avatar-title" data-testid="avatar-panel-title">
                {popoverContent.title}
              </h3>
              <div className="sv-avatar-rows">
                {popoverContent.rows.map((row) => (
                  <div
                    key={row.label}
                    className="sv-avatar-row"
                    data-testid={`avatar-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span>{row.label}</span>
                    <strong className={row.mono ? 'mono' : undefined}>{row.value}</strong>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}
